import { Container, Row, Column } from '@algbra/ui/components/Grid/Grid'
import { useState, useEffect } from 'react'
import Icon from '@algbra/ui/components/Icon/Icon'
import FreeText from '@algbra/ui/components/FreeText/FreeText'
import Heading from '@algbra/ui/components/Heading/Heading'
import Input from '@algbra/ui/components/Input/Input'
import Permalink from '@algbra/ui/components/Permalink/Permalink'
import {
  MEDIA_QUERY_MOBILE,
  useMediaQuery,
} from '@algbra/ui/hooks/useMediaQuery'
import { getIconByName } from '@algbra/ui/icons'
import { resolveAsImage, resolveAsAttachment } from '@algbra/ui/resolvers'
import { GatsbyImage } from 'gatsby-plugin-image'
import { GatsbyResolvedAsset } from '@algbra/ui/types/image'
import { mapIndexed } from 'rambdax'
import React from 'react'
import axios from 'axios'
import classNames from 'classnames'
import Intercom from './Intercom'

import styles from './Help.module.scss'

export type HelpCategories = {
  categoryName: string
  categoryIcon: GatsbyResolvedAsset
  categoryLink: string
}

export type HelpChannels = {
  channelDesc: string
  channelIcon: GatsbyResolvedAsset
  channelName: string
}

export type ZoneHelpProps = {
  __typename: 'StrapiGQL_ComponentZonesHelp'
  boxDescription: string
  boxList: string
  boxTitle: string
  boxImage: GatsbyResolvedAsset
  disclaimer: string
  helpCategories?: HelpCategories[]
  searchPlaceholder: string
  subDescription: string
  subTitle: string
  helpCategoryBoxDescription: string
  helpCategoryBoxTitle: string
  helpChannels?: HelpChannels[]
}

export default function ZoneHelp({
  boxDescription,
  boxList,
  boxTitle,
  boxImage,
  disclaimer,
  helpCategories,
  searchPlaceholder,
  subDescription,
  subTitle,
  helpCategoryBoxDescription,
  helpCategoryBoxTitle,
  helpChannels,
}: ZoneHelpProps) {
  const [searchTerm, setSearchTerm] = useState('')
  const [systemStatus, setSystemStatus] = useState('')
  const [systemStatusIndicator, setsystemStatusIndicator] = useState('')

  const search = value => {
    window.open('https://help.algbra.com/en/?q=' + value, '_blank')
  }

  const handlePress = e => {
    if (e.key === 'Enter') {
      search(searchTerm)
    }
  }

  useEffect(() => {
    axios
      .get('https://algbra.statuspage.io/api/v2/status.json')
      .then(response => {
        setSystemStatus(response.data.status.description)
        setsystemStatusIndicator(response.data.status.indicator)
      })
  }, [])

  return (
    <div>
      <Intercom />
      <Container>
        <div className={styles.statusContainer}>
          <div
            className={classNames(
              styles.status,
              styles[`ind_${systemStatusIndicator}`]
            )}
          >
            <FreeText className={styles.key}>System Status</FreeText>
            <span className={styles.indicator}></span>
            <FreeText className={styles.value}>{systemStatus}</FreeText>
          </div>
        </div>
        <div className={styles.inputWrapper}>
          <button onClick={e => search(searchTerm)}>
            <Icon
              className={styles.searchIcon}
              icon={getIconByName('search')}
              size={24}
            />
          </button>
          <Input
            name="search"
            type="text"
            className={styles.searchInputContainer}
            controlClassName={styles.searchInput}
            placeholder={searchPlaceholder}
            onKeyPress={handlePress}
            onChange={e => setSearchTerm(e.currentTarget.value)}
          />
        </div>
      </Container>
      <div className={styles.helpCategories}>
        <Container>
          {helpCategoryBoxTitle && (
            <Heading className={styles.title}>{helpCategoryBoxTitle}</Heading>
          )}
          {helpCategoryBoxDescription && (
            <FreeText className={styles.description}>
              {helpCategoryBoxDescription}
            </FreeText>
          )}
          <div className={styles.categories}>
            <Row>
              {mapIndexed(
                ({ categoryIcon, categoryName, categoryLink }, index) => {
                  return (
                    <Column key={index} size={12} sm={4}>
                      <Permalink
                        to={categoryLink}
                        className={styles.logo}
                        isExternal={true}
                        isBlank={true}
                      >
                        <div className={styles.catCont}>
                          <div className={styles.imgContainer}>
                            <img
                              src={resolveAsAttachment(categoryIcon)}
                              alt={categoryName}
                              loading="lazy"
                            />
                          </div>
                          <FreeText className={styles.catName}>
                            {categoryName}
                          </FreeText>
                        </div>
                      </Permalink>
                    </Column>
                  )
                },
                helpCategories || []
              )}
            </Row>
          </div>
        </Container>
      </div>
      <Container>
        <div className={styles.box}>
          <Row>
            <Column className={styles.textContainer} size={12} sm={6}>
              <div>
                <Heading className={styles.title}>{boxTitle}</Heading>
                <FreeText className={styles.description}>
                  {boxDescription}
                </FreeText>
                <FreeText className={styles.listing} innerHTML={boxList}>
                  {boxList}
                </FreeText>
              </div>
            </Column>
            <Column>
              <div className={styles.imageContainer}>
                <div className={styles.imageWithShadow}>
                  <GatsbyImage
                    image={resolveAsImage(boxImage)}
                    alt={boxTitle}
                    className={styles.itemImage}
                    loading="lazy"
                  />
                </div>
              </div>
            </Column>
          </Row>
        </div>
        <div className={styles.helpChannel}>
          <Heading size={2.25} className={styles.subTitle}>
            {subTitle}
          </Heading>
          <FreeText className={styles.subDescription}>
            {subDescription}
          </FreeText>
          <Row>
            {mapIndexed(({ channelName, channelDesc, channelIcon }, index) => {
              return (
                <Column key={index} size={12} sm={4}>
                  <div className={styles.channelCont}>
                    <img
                      src={resolveAsAttachment(channelIcon)}
                      alt={channelName}
                      loading="lazy"
                    />
                    <FreeText className={styles.channelName}>
                      {channelName}
                    </FreeText>
                    <FreeText
                      innerHTML={channelDesc}
                      className={styles.channelDescription}
                    >
                      {channelDesc}
                    </FreeText>
                  </div>
                </Column>
              )
            }, helpChannels || [])}
          </Row>
        </div>
        {disclaimer && (
          <FreeText className={styles.disclaimer} innerHTML={disclaimer}>
            {disclaimer}
          </FreeText>
        )}
      </Container>
    </div>
  )
}
